<!--    The Button that represents a Device / CompoundMeter in the Device List 
        In The Case of CompoundMeters it only displays The Data of the main Watermeter in that configuration    -->
<!--Copy right - © 2024 tecera gmbh-->

<script>
    import { slide, fade } from "svelte/transition";
    import {
        selected_device,
        modal_mode,
        current_device_data,
        is_modal_open,
    } from "../../stores.js";
    import { find_main_meter } from "../../scripts/utils.js";
    import DeviceButtonDataLine from "./buttons_parts/DeviceButtonDataLine.svelte";
    import { onMount } from "svelte";
    import { dragHandle } from "svelte-dnd-action";
    import { stop_propagation } from "svelte/internal";

    export let device_config;
    export let multiple_devices = false;
    export let is_dummy_device = false;
    export let opens_modal = null; // If set, the button will open a modal instead of selecting the device
    export let open_modal;
    export let is_sorting = false;

    let is_open = false;
    let buttonElement;
    let main_meter;
    let status = "Aktiv";

    onMount(() => {
        if (multiple_devices && device_config) {
            main_meter = find_main_meter(device_config);
        }
    });

    $: {
        if (multiple_devices && $selected_device) {
            main_meter = find_main_meter(device_config);
        }
    }

    $: if (!is_dummy_device) {
        if (multiple_devices) {
            if (main_meter) {
                status = main_meter.active === 1 ? "Aktiv" : "Inaktiv";
            } else {
                status = "Inaktiv";
            }
        } else {
            status = device_config.active === 1 ? "Aktiv" : "Inaktiv";
        }
    } else {
        status = device_config.status === "active" ? "Aktiv" : "Inaktiv";
    }

    $: {
        if ($selected_device === device_config) {
            is_open = true;
            if (buttonElement) {
                buttonElement.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                }); //TODO: disable on low width
            }
        } else {
            is_open = false;
        }
    }
    let last_values = {};
    let last_sum = 0;

    $: if ($selected_device === device_config && $current_device_data) {
        last_values = $current_device_data.map((data) => {
            return { id: data.config_id, data: data.last_data?.meter_reading };
        });
        last_sum = sum_data();
    }

    function sum_data() {
        let sum = 0;
        last_values.forEach((data) => {
            sum += data.data;
        });
        return sum;
    }
    //TODO: clean up code
</script>

{#if !multiple_devices || main_meter}
    <div
        id="device-button-{device_config.id}"
        bind:this={buttonElement}
        class="list-group-item"
        style="padding:0;font-size:smaller;"
    >
        <div
            role="button"
            tabindex="0"
            class="row bold no-gutters list-group-item-action device-button-header"
            style={is_sorting ? "cursor: auto;opacity:0.85;" : ""}
            on:keypress={(e) => {
                if (e.key === "Enter" && !is_sorting) {
                    if ($selected_device === device_config) {
                        is_open = !is_open;
                    } else {
                        $selected_device = device_config;
                    }
                }
            }}
            on:click={() => {
                if (is_sorting) return;
                if ($selected_device === device_config) {
                    is_open = !is_open;
                } else {
                    $selected_device = device_config;
                }
            }}
        >
            {#if is_sorting}
                <div
                    class="col-2 text-left icon-container-left"
                    style="padding-right:0px;"
                >
                    <span use:dragHandle class="css-bars-icon"><div></div></span
                    >
                </div>
            {:else}
            <div class="col-2 icon-container-left">
                    {#if is_open}
                        <div class="icon-container">
                            <i
                                class="fa-solid fa-chevron-down arrow"
                                style="width: 1rem; height: 1rem;"
                            ></i>
                        </div>
                    {:else}
                        <div class="icon-container">
                            <i
                                class="fa-solid fa-chevron-right arrow"
                                style="width: 1rem; height: 1rem;"
                            ></i>
                        </div>
                    {/if}
            </div>
            {/if}
            <div class="col-7 text-left align-items-center">
                <p style="white-space: pre-line;" class="mb-0">
                    {multiple_devices
                        ? main_meter.name
                        : device_config.name || "unbennantes Gerät"}
                </p>
            </div>
            <div class="col-3 text-right align-items-center">{status}</div>
        </div>

        {#if is_open && !is_sorting}
            <div
                transition:slide|local
                id="device-button-data-{device_config.id}"
                style="margin-left:auto;cursor-events: none;"
            >
                <div class="gray container">
                    {#if !multiple_devices}
                        <DeviceButtonDataLine
                            label="Dev_eui:"
                            value={device_config.dev_eui
                                ? device_config.dev_eui
                                : device_config.box_id}
                        />
                    {/if}
                    <DeviceButtonDataLine
                        label="Stadt:"
                        value={multiple_devices
                            ? main_meter.location
                            : device_config.location}
                    />
                    <DeviceButtonDataLine
                        label="Ortsteil:"
                        value={multiple_devices
                            ? main_meter.district
                            : device_config.district}
                    />
                    <DeviceButtonDataLine
                        label="Straße:"
                        value={multiple_devices
                            ? main_meter.street
                            : device_config.street}
                    />
                    <DeviceButtonDataLine label="Status:" value={status} />
                    <DeviceButtonDataLine
                        label="Ergänzung:"
                        value={multiple_devices
                            ? main_meter.comment
                            : device_config.comment}
                    />
                    <DeviceButtonDataLine
                        label="Bemerkung zum Einbauort:"
                        value={multiple_devices
                            ? main_meter.comment_location
                            : device_config.comment_location}
                    />
                    {#if multiple_devices}
                        {#if main_meter.compound_role === "main"}
                            {#each device_config.watermeterconfigs as meter}
                                {#if meter.compound_role === "sub"}
                                    <div class="row">
                                        <div class="col-6">
                                            {"Nebenzähler-ID:"}
                                        </div>
                                        <div class="col-6">
                                            {meter.alt_id}
                                        </div>
                                    </div>
                                {/if}
                            {/each}
                            <div class="row">
                                <div class="col-6">
                                    {"Zählerstand Gesamt:"}
                                </div>
                                <div class="col-6">
                                    {#if last_sum === 0}
                                        {""}
                                    {:else}
                                        {last_sum.toFixed(3) + " m³"}
                                    {/if}
                                </div>
                            </div>

                            {#each device_config.watermeterconfigs as meter}
                                <div class="row">
                                    <div class="col-6">
                                        {meter.compound_role === "main"
                                            ? "Hauptzähler:"
                                            : "Nebenzähler:"}
                                    </div>
                                    <div class="col-6">
                                        {last_values.find(
                                            (data) => data.id === meter.id,
                                        )?.data === undefined
                                            ? ""
                                            : last_values
                                                  .find(
                                                      (data) =>
                                                          data.id === meter.id,
                                                  )
                                                  ?.data.toFixed(3) + " m³"}
                                    </div>
                                </div>
                            {/each}
                        {:else}
                            {#each device_config.watermeterconfigs as meter}
                                <DeviceButtonDataLine
                                    label={"Haupzähler:"}
                                    value={last_values.find(
                                        (data) => data.id === meter.id,
                                    )?.data === undefined
                                        ? ""
                                        : last_values.find(
                                              (data) => data.id === meter.id,
                                          )?.data + " m³"}
                                />
                            {/each}
                        {/if}
                    {/if}

                    <div class="row" style="">
                        <div
                            class="col-12"
                            style="text-align: right; margin-top: 0.75rem; margin-bottom: 0.75rem;"
                        >
                            <button
                                class="btn btn-primary"
                                on:click={() => {
                                    $modal_mode = "edit";
                                    open_modal = opens_modal;
                                    $is_modal_open = true;
                                }}
                                ><i class="fa-solid fa-pen-to-square"></i> Bearbeiten</button
                            >
                        </div>
                    </div>
                </div>
            </div>
        {/if}
    </div>
{/if}
